import { default as Modal } from 'antd/es/modal';
import { default as Skeleton } from 'antd/es/skeleton';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { hasValue } from 'utils/hasValue';

import { getCollectionOrderReportsUuid } from 'api';
import type { OrderReports } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useAsyncMemo } from 'hooks/useAsyncMemo';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { DownloadThinIcon } from '@atoms/Icon/DownloadThinIcon';

import { DownloadPayinReportsCard } from './DownloadPayinReportsCard';

import {
  DOWNLOAD_PAYINS_CSV_BUTTON_TEST_ID,
  EXPORTED_PAYINS_MODAL_TEST_ID
} from '../../constants';

type DownloadPayinReportsDialogProps = {
  downloadReport: (report: OrderReports) => void;
};

export const DownloadPayinReportsDialog = ({
  downloadReport
}: DownloadPayinReportsDialogProps) => {
  const organisationId = useOrganisationId<number>();
  const [searchParams] = useSearchParams();
  const openNotification = useOpenNotification();
  const uuid = searchParams.get('download_orders') || '';
  const [open, setOpen] = useState<boolean>(uuid !== '');
  const reportInfo = useAsyncMemo(async () => {
    if (!uuid) return undefined;
    try {
      const retval = await getCollectionOrderReportsUuid(organisationId, uuid);
      return retval.data;
    } catch {
      openNotification({
        kind: 'error',
        message: 'Could not find Pay-in Report with that uuid',
        key: 'fetch-payin-report-info'
      });
      setOpen(false);
    }
  }, [organisationId, uuid]);

  const handleDownload = (report: OrderReports) => {
    downloadReport(report);
    setOpen(false);
  };

  return (
    <Modal
      className="top-1/4"
      data-testid={EXPORTED_PAYINS_MODAL_TEST_ID}
      title={
        <div className="mb-6 flex flex-row">
          <DownloadThinIcon />
          <div className="pl-4">Download pay-in report</div>
        </div>
      }
      open={open}
      onCancel={() => setOpen(false)}
      footer={
        <PrimaryButton
          className="mt-4 w-full"
          data-testid={DOWNLOAD_PAYINS_CSV_BUTTON_TEST_ID}
          disabled={!hasValue(reportInfo)}
          onClick={() => {
            reportInfo && handleDownload(reportInfo);
          }}
        >
          Download CSV report
        </PrimaryButton>
      }
      width={336}
    >
      {reportInfo ? (
        <DownloadPayinReportsCard
          createdAfter={reportInfo.created_after}
          createdBefore={reportInfo.created_before}
          virtualAccountNumber={
            reportInfo.virtual_account_number || 'All accounts'
          }
          status={reportInfo.status}
        />
      ) : (
        <Skeleton />
      )}
    </Modal>
  );
};
